import { toast } from "../../app";
import { readFileBuffer } from "../file-system-loader";
import {
  CADHandler,
  LineworkLayer,
  LineworkFile
} from ".";
import { Asset, LineworkLayerData } from "../../redux/assets-slice";
import { pathWithExtension } from "../../utilities";
import { t } from "../../localization";
import { Box3 } from "three";
import { DataProjectionCoordinate } from "../projections";


class SHPLayer extends LineworkLayer {
  constructor(file: SHPFile, layer: LineworkLayerData) {
    super(file, layer);
  }
}

export class SHPFile extends LineworkFile {
  constructor(cadHandler: CADHandler, asset: Asset) {
    super(cadHandler, asset);
    this.addLayers(asset.data);
  }

  get shpPath() {
    return pathWithExtension(this.path, "shp");
  }

  get dbfPath() {
    return pathWithExtension(this.path, "dbf");
  }

  getByName(name: string) {
    return this.layers.find(layer => layer.name === name);
  };

  async load() {
    this.loading = true;
    this.loaded = false;
    this.updatePercent(0);

    try {
      const shp = await readFileBuffer(this.shpPath);
      const dbf = await readFileBuffer(this.dbfPath);
      const {bounds, features, numInvalid}
        = await this.parseFromBuffer(shp, dbf);

      const layerNames = Object.keys(features);

      if (numInvalid) {
        toast.warning(t('toast.model_feature_error', {
          count: numInvalid
        }));
      }

      if (this.validBounds(bounds)) {
        this.boundingBox = new Box3(
          new DataProjectionCoordinate(bounds.min).toScene(),
          new DataProjectionCoordinate(bounds.max).toScene()
        );

        if (!this.saved) {
          this.viewer.zoomToSceneExtent(this.boundingBox, false);
        }
      }

      for (let i=0; i<layerNames.length; i++) {
        this.updatePercent(100 * (i + 0.5) / layerNames.length);
        const layerName = layerNames[i];
        const data = features[layerName];
        const layer = this.getByName(layerName);
        await layer.addNewGeometry(data);
      };
    } catch(err) {
      console.error(err);
      this.error = true;
      toast.error(t("toast.model-load-error", {
        name: this.name
      }));
    }

    this.loaded = true;
    this.loading = false;
    this.updatePercent(100.0);
  };

  async parseFromBuffer(shp, dbf): Promise<any> {
    return new Promise((resolve, reject) => {
      const workerURL = "workers/shp-reader.js";
      let worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);

      worker.onmessage = event => {
        const {success, response} = event.data;

        if (success) {
          resolve(response);
        } else {
          console.error(response);
          reject(response);
        }
      };

      const data = {shp, dbf, type: "features"};
      worker.postMessage(data, [data.shp, data.dbf]);
    });
  };

  addLayers(layers) {
    layers.forEach(data => {
      let newLayer = new SHPLayer(this, data);
      this.layers.push(newLayer);
    });
  }

  /** Update visibility and colors */
  updateProperties(asset: Asset) {
    this.visible = asset.visible;

    const layers = asset.data as LineworkLayerData[];

    layers.forEach(data => {
      const layer = this.getByName(data.name);
      const visible = this.visible ? data.visible : false;
      layer.setColor(data.color);
      layer.setVisibility(visible);
    });

    this.updatePicker();
  }

  destroy() {
    this.enabled = false;

    for (let layer of this.layers) {
      layer.destroy();
    }

    this.layers = [];
  }
}
